import React, {DragEvent, FC, PropsWithChildren, useEffect} from 'react';
import {DragPreviewOptions, useDrag} from 'react-dnd';

import {supportsDownloadDragSource} from '../../../../../../../libs/shared/helpers/src/environment/platformHelpers';
import conversationDragAndDrop1 from '../../../../../../assets/images/conversationDragAndDrop1.svg';
import {addDownloadToDragEvent} from '../../../../../../helpers/dragAndDropHelpers';
import {emlFileName} from '../../../../../../helpers/models/messageHelpers';
import {LoadedMessageModel, MessageTypesEnum} from '../../../../../../models/api/messageModel';
import {InterfaceEventsEnum, trackInterfaceEvent} from '../../../../../../services/tracking';
import {
  addNativeAttachmentsToDragEvent,
  DraggedAttachmentItemTypesEnum,
} from '../../../../../common/attachments/attachmentsDragAndDrop';

/*
 * Constants & Types.
 */

const MessageViewerEmailFullHeaderDragType = 'MESSAGE_VIEWER_EMAIL_FULL_HEADER';

/*
 * Props.
 */

interface MessageViewerEmailFullHeaderDragSourceProps {
  message: LoadedMessageModel;
}

/*
 * Component.
 */

function getDownloadOptions(message: LoadedMessageModel) {
  return (
    message.type === MessageTypesEnum.EMAIL && {
      name: emlFileName(message),
      url: `${location.origin}${message.messageUrl}/eml`,
    }
  );
}

export const MessageViewerEmailFullHeaderDragSource: FC<
  PropsWithChildren<MessageViewerEmailFullHeaderDragSourceProps>
> = (props) => {
  const {message, children} = props;

  const [, drag, preview] = useDrag({
    type: MessageViewerEmailFullHeaderDragType,
    item: {},
  });

  useEffect(() => {
    const previewOptions: DragPreviewOptions = {
      offsetX: 10,
      offsetY: 10,
    };

    const previewImage = new Image();
    previewImage.src = conversationDragAndDrop1;
    previewImage.onload = () => preview(previewImage, previewOptions);
  }, [preview]);

  const onDragStart = (e: DragEvent) => {
    if (!e.dataTransfer) {
      return;
    }

    e.dataTransfer.effectAllowed = 'copy';

    const options = getDownloadOptions(message);
    if (!options) {
      return;
    }

    if (supportsDownloadDragSource()) {
      addDownloadToDragEvent(e, options);
    }

    addNativeAttachmentsToDragEvent(e, {
      type: DraggedAttachmentItemTypesEnum.MESSAGES,
      messages: [{messageUid: message.uid, emlFileName: options.name}],
    });
  };

  const onDragEnd = (e: DragEvent) => {
    if (!e.dataTransfer || e.dataTransfer.dropEffect !== 'copy') {
      return;
    }

    trackInterfaceEvent(InterfaceEventsEnum.MESSAGE_DRAG_AND_DROP_TO_EML);
  };

  return (
    <div ref={drag} onDragStart={onDragStart} onDragEnd={onDragEnd}>
      {children}
    </div>
  );
};
